/**
 * SOUND-619 — turn a pasted block of underwriter text into structured pends.
 *
 * The workflow this serves: a VA receives a pend list from an underwriter (email
 * or portal) and pastes it in whole. Getting that into N structured items is the
 * slow, error-prone part of their day, so the parser does the first pass and the
 * VA reviews before anything is sent.
 *
 * PARSING IS A SUGGESTION, NEVER AUTHORITATIVE. Every parsed row is editable in
 * the composer before submit, because real pend text defeats any tidy rule:
 *
 *  - Descriptions are long PROSE, not one-liners. Real example, one single pend:
 *      "Correct the Privacy Policy as they did with the other legal sections
 *       regarding Shopify. remove any references ... The merchant may make a
 *       separate section for Shopify legal only, if required."
 *    Splitting on every newline would shred that into fragments.
 *
 *  - A leading "1." is NOT reliably a list marker. Another real single pend
 *    begins: "1. Clarify why merchant is offering a VIP membership when they
 *    only sell 3 items." So marker-splitting is used only when the text looks
 *    like a list throughout, never opportunistically.
 *
 * Strategy, most-confident first:
 *   1. Blank-line separated blocks  → one pend per block (handles long prose)
 *   2. Every non-empty line marked  → one pend per marked line (a real list)
 *   3. Otherwise                    → one pend per non-empty line
 */

// Leading list markers: "1." "1)" "(1)" "-" "*" "•" "–" "—"
const MARKER = /^\s*(?:\(?\d+[.)]|[-*•–—])\s+/;

/**
 * SOUND-621 — the category vocabulary is now CONTROLLED and served by
 * GET /pends/categories. This list is gone deliberately: a frontend copy is
 * exactly how 36 spellings of eight categories happened. The composer fetches it.
 *
 * What stays here is the GUESS, which maps pasted prose onto a category KEY.
 */

// Conservative keyword → CATEGORY KEY guesses, checked in order. Only patterns
// that were unambiguous in the real prod data; anything else falls through to ""
// so the operator picks. A wrong guess is worse than a blank — it groups a pend
// under something it is not, and the operator has no reason to look twice at a
// field that is already filled in.
//
// Order matters: bank evidence is checked BEFORE the general document pattern,
// because "voided check" also matches /document/-adjacent words and the more
// specific category is the useful one. That is the same call SOUND-621's
// historical mapping made for " Document Request - Voided Check".
const CATEGORY_HINTS = [
  [/voided check|bank letter|bank statement|dda|routing|account ending/i, "bank_verification"],
  [/id verification|drivers? licen[cs]e|passport|photo id/i, "id_verification"],
  [/login|credential|vip access|test account|member (?:area|portal)/i, "test_login"],
  [/statement|invoice|agreement|signed application|docusign|proof of|w-?9|licen[cs]e|certificate|document/i, "document_request"],
  [/website|privacy policy|refund policy|terms|product description|landing page|checkout page|domain/i, "website_edit"],
  [/\?$/, "question"],
];

/**
 * Best-effort CATEGORY KEY from the description. "" when nothing is confident.
 *
 * Returns a key, not a label: the label belongs to the served vocabulary, and
 * two spellings of the same category is the bug SOUND-621 removed.
 */
export function guessPendCategory(description = "") {
  for (const [pattern, key] of CATEGORY_HINTS) {
    if (pattern.test(description)) return key;
  }
  return "";
}

/**
 * Split a description that leads with an explicit "Label: rest" prefix.
 * Only splits on a SHORT prefix — "Please provide a voided check: see attached"
 * must not become a label of "Please provide a voided check".
 *
 * The label becomes the free-text DETAIL, never a category: an operator's prefix
 * is their words, and promoting arbitrary text into a controlled field is what
 * the vocabulary exists to stop.
 */
function splitLabelled(text) {
  const at = text.indexOf(":");
  if (at === -1) return null;
  const label = text.slice(0, at).trim();
  const rest = text.slice(at + 1).trim();
  // A type is a short noun phrase, and there must be something left over.
  if (!rest || label.length > 40 || label.split(/\s+/).length > 5) return null;
  return { detail: label, description: rest };
}

const stripMarker = (line) => line.replace(MARKER, "").trim();

/**
 * Parse pasted text into pend drafts.
 * @param {string} text
 * @returns {{type: string, description: string}[]}
 */
export function parsePendText(text) {
  if (typeof text !== "string" || !text.trim()) return [];

  const normalized = text.replace(/\r\n?/g, "\n").trim();

  // 1. Blank-line separated blocks. Only when there is more than one block —
  //    otherwise fall through so a single prose blob still gets line handling.
  const blocks = normalized.split(/\n\s*\n+/).map((b) => b.trim()).filter(Boolean);
  let chunks;
  if (blocks.length > 1) {
    // Within a block, collapse the newlines: it is one pend's prose.
    chunks = blocks.map((b) => stripMarker(b).replace(/\s*\n\s*/g, " "));
  } else {
    const lines = normalized.split("\n").map((l) => l.trim()).filter(Boolean);
    // 2. A real list — every line carries a marker.
    // 3. Otherwise one pend per line.
    chunks = lines.length > 1 && lines.every((l) => MARKER.test(l))
      ? lines.map(stripMarker)
      : lines.map(stripMarker);
  }

  return chunks
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((chunk) => {
      const labelled = splitLabelled(chunk);
      // The guess runs on the REMAINING text either way, so a labelled row still
      // gets a category suggestion instead of arriving blank.
      if (labelled) {
        return {
          category: guessPendCategory(labelled.description),
          detail: labelled.detail,
          description: labelled.description,
        };
      }
      return { category: guessPendCategory(chunk), detail: "", description: chunk };
    });
}

export default parsePendText;
